import { useCallback, useEffect, useMemo } from 'react';

import { WidgetViewMode } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { ProgressMeasurement } from 'components/common/GlobalFilterComponent/components/progressMeasurementFilter/progressMeasurementFilter';

import { isBetweenRange } from 'components/Dashboards/Project/Components/helper';
import { useUpdateIfReady } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useUpdateIfReady';
import { UseDelayedActivitiesProvider } from 'components/widgets/DelayedActivities/DelayedActivities.provider';
import { TDelayTypeOptions } from 'components/widgets/DelayedActivities/DelayedActivities.utils';

const FILTER_LIST: ProgressMeasurement[] = [
    'activityCompleted',
    'activityCompletedCriticality',
    'activitySubmittedCompletion',
    'activitySubmittedCompletionCriticality',
];

interface Props {
    ui: UseDelayedActivitiesProvider['ui'];
    api: UseDelayedActivitiesProvider['api'];
    title: string;
    subTitle: string;
    table: UseBaseTable['table'];
    widgetViewMode?: WidgetViewMode;
    setIsPayloadReady?: (isPayloadReady: boolean) => void;
}

export const useDelayedActivitiesLogic = ({
    ui,
    api,
    title,
    subTitle,
    table,
    widgetViewMode,
    setIsPayloadReady,
}: Props) => {
    const isPayloadReady = () => {
        const currentPayload = api.getTopOverrunActivities.payload;
        if (!currentPayload) return;
        if (
            currentPayload.comparedVersionId === undefined ||
            currentPayload.id === undefined ||
            currentPayload.id.length === 0 ||
            currentPayload.id[0] === undefined ||
            currentPayload.id[0] === null
        ) {
            return;
        }
        setIsPayloadReady && setIsPayloadReady(true);
    };

    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    useUpdateIfReady({ isPayloadReadyCallback: isPayloadReady, widgetViewMode: widgetViewMode });
    const numOfMonthsToBeSelected: string = useMemo(() => {
        return ui.state.numOfMonths ? ui.state.numOfMonths.value : ui.state.nextFilter;
    }, [ui.state.numOfMonths, ui.state.nextFilter]);

    const notApplicable = useMemo(() => {
        return (
            FILTER_LIST.includes(ui.state.progressMeasurement) && Boolean(ui.state.resourceType || ui.state.resource)
        );
    }, [ui.state.resourceType, ui.state.progressMeasurement, ui.state.resource]);

    const handleStatus = useCallback((value: readonly ISelectOption<string>[]) => {
        const selected = value.map((item) => item.value);
        ui.setters.setStatus(value);

        sendEventWithDimensions({
            category: 'Activities Table(s)',
            action: 'Status Filter click',
            label: selected.length > 0 ? selected.join(',') : 'Deselect All',
        });
    }, []);

    const handleDelayType = useCallback((value: ISelectOption<TDelayTypeOptions[]> | null) => {
        ui.setters.setDelayTypeStatus(value);
        if (value) {
            sendEventWithDimensions({
                category: 'Activities Table(s)',
                action: 'Status Filter click',
                label: value.label,
            });
        }
    }, []);

    const handleStatusType = (value: readonly ISelectOption<string>[]) => {
        ui.setters.setStatusFilter(value);
    };

    const filteredRows = useMemo(() => {
        return api.getTopOverrunActivities.instance.data && !notApplicable
            ? api.getTopOverrunActivities.instance.data.delayDrivers.filter((item) =>
                  isBetweenRange({
                      numOfMonths: Number(numOfMonthsToBeSelected),
                      startDate: item.currentStartDate,
                      endDate: item.currentFinishDate,
                  }),
              )
            : [];
    }, [api.getTopOverrunActivities.instance.data, numOfMonthsToBeSelected, notApplicable]);

    const handleTimePeriodClick = (filter: ISelectOption<string>) => {
        const value = filter.value;
        sendEventWithDimensions({
            category: 'Activities Table(s)',
            action: 'Next Selection',
            label: value,
        });
        ui.setters.setNextFilter(value);
        ui.setters.setNumOfMonths(filter);
    };

    const handleCriticalPath = (event) => {
        const isChecked = event.target.checked;
        ui.setters.setIsCriticalPath(isChecked);
    };

    const handleAPICsvExport = () => {
        api.extractDelayedActivitiesToCsv.instance.mutate(
            { ...api.extractDelayedActivitiesToCsv.payload, fileName: `${title}_${subTitle}` },
            {
                onSuccess: () => {
                    handleSnackBar('File Successfully Downloaded', 'success');

                    sendEventWithDimensions({
                        category: 'Export',
                        action: 'CSV',
                        label: `${title} ${subTitle}`,
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    const counter = !notApplicable
        ? api.getTopOverrunActivities.instance.data?.numOfRows
            ? `(${api.getTopOverrunActivities.instance.data?.numOfRows})`
            : undefined
        : undefined;

    useEffect(() => {
        let totalPages = 0;
        if (api.getTopOverrunActivities.instance.data && !notApplicable) {
            totalPages = api.getTopOverrunActivities.instance.data.numOfPages;
        }
        table.setTotalPages(totalPages);
    }, [api.getTopOverrunActivities.instance.data, notApplicable]);

    useEffect(() => {
        table.setPagination((prev) => ({ ...prev, page: 1 }));
    }, [
        ui.state.latestVersionId,
        ui.state.compareVersionId,
        ui.state.sorting,
        ui.state.activityCodes,
        ui.state.milestoneFragnet,
        ui.state.tags,
        ui.state.wbs,
        ui.state.statusFilter,
        ui.state.status,
        ui.state.delayTypeStatus,
        ui.state.isCriticalPath,
        ui.state.resource,
        ui.state.resourceType,
    ]);
    return {
        logic: {
            notApplicable,
            handleStatus,
            handleDelayType,
            handleStatusType,
            filteredRows,
            handleTimePeriodClick,
            handleCriticalPath,
            handleAPICsvExport,
            counter,
        },
    };
};
